import Decimal, {DecimalSource} from './break_infinity';
import {Vector} from './Vector';
import {Action} from './action';
import {Actor} from './actor';
import {Creature} from './creature';
import {ri} from './runner-idle';
import {StatusEffect} from './status-effect';
import {ResourceName} from './types/runner-idle';
import {randFloat} from './utils';

export const makeAddResourceAction: (
  resourceName: ResourceName,
  amount: DecimalSource,
  newCapacity?: Decimal
) => Action = (
  resourceName: ResourceName,
  amount: DecimalSource,
  newCapacity: DecimalSource = amount
) => ({
  act(this: Creature) {
    this.addResource(resourceName, amount, newCapacity);
  },
});

export const stepAction: Action = {
  act(this: Creature) {
    const nextPosition = this.position$.value.plus(
      ri.pointHasTag(this.position, 'multiplySpeed')
        ? this.heading.times(
            this.stats.speed?.value?.times(
              Object.values(ri.landmarks)
                .filter((l) => l.containsPoint(this.position))
                .reduce(
                  (acc, landmark) => acc.times(landmark.speed),
                  new Decimal(1)
                )
            ) ?? new Decimal(0)
          )
        : this.heading.times(this.stats.speed?.value ?? new Decimal(0))
    );
    this.moveTo(nextPosition);
  },
};

export const makeTweakHeadingAction: (maxOffsetRadians?: number) => Action = (
  maxOffsetRadians: number = Math.PI
) => ({
  act(this: Creature) {
    // Randomly change heading
    this.heading = this.heading.rotated(
      randFloat(-maxOffsetRadians, maxOffsetRadians)
    );
  },
});

export const makeAvoidPlayerAction: (
  avoidMaxRange: Decimal,
  avoidStrength: Decimal
) => Action = (avoidMaxRange: Decimal, avoidStrength: Decimal) => ({
  act(this: Creature) {
    if (this.position.distance(ri.position).lt(avoidMaxRange)) {
      // They're avoiding you
      this.heading = this.position.minus(ri.position).normalized;
      if (this.heading.magnitude.eq(0)) {
        this.heading = Vector.random(1);
      }
    }
  },
});

export const trackPlayerAction: Action = {
  act(this: Creature) {
    // They're tracking you
    this.heading = ri.position.minus(this.position).normalized;
    if (this.heading.magnitude.eq(0)) {
      this.heading = Vector.random(1);
    }
  },
};

export const makeTrackPlayerAction: (
  trackMaxRange: Decimal,
  trackStrength: Decimal
) => Action = (trackMaxRange: Decimal, trackStrength: Decimal) => ({
  act(this: Creature) {
    if (this.position.distance(ri.position).lt(trackMaxRange)) {
      // They're tracking you
      this.heading = ri.position.minus(this.position).normalized;
      if (this.heading.magnitude.eq(0)) {
        this.heading = Vector.random(1);
      }
    }
  },
});

export const makeAtVisionRangeAction = (
  border: DecimalSource = '',
  marginWidth: DecimalSource = 2,
  inMarginAction: Action = {act() {}},
  trackMaxRange: DecimalSource = 100
) => ({
  act(this: Creature) {
    if (border === '') {
      border = this.stats.speed?.value?.plus(5) ?? new Decimal(10);
    }
    const distance = this.position.distance(ri.position);
    // Avoid while inside vision range and for a border outside it
    if (distance.lt(ri.visionRange.plus(border))) {
      this.heading = this.position.minus(ri.position).normalized;
      if (this.heading.magnitude.eq(0)) {
        this.heading = Vector.random(1);
      }
    }
    // Track outside the bordered area + margin
    else if (
      distance.gt(ri.visionRange.plus(border).plus(marginWidth)) &&
      distance.lt(trackMaxRange)
    ) {
      this.heading = ri.position.minus(this.position).normalized;
      if (this.heading.magnitude.eq(0)) {
        this.heading = Vector.random(1);
      }
    } else {
      this.heading = new Vector();
      inMarginAction.act.call(this);
    }
  },
});

export const makeSpawnAction: (overrides: {
  spawnTemplate?: Partial<Creature> | 'self';
  spawnRange?: Decimal;
  spawnInterval?: Decimal;
  spawnRate?: Decimal;
  spawnTimeLeft?: Decimal;
  spawnMaxChildren?: Decimal;
}) => Action = ({
  spawnTemplate = 'self',
  spawnRange = new Decimal(0),
  spawnInterval = new Decimal(1),
  spawnTimeLeft = spawnInterval,
  spawnMaxChildren = new Decimal(10),
}) => {
  const spawnTemplateSymbol = Symbol('spawnTemplate');
  const spawnRangeSymbol = Symbol('spawnRange');
  const spawnIntervalSymbol = Symbol('spawnInterval');
  const spawnTimeLeftSymbol = Symbol('spawnTimeLeft');
  const spawnMaxChildrenSymbol = Symbol('spawnMaxChildren');

  return {
    properties: {
      [spawnTemplateSymbol]: spawnTemplate,
      [spawnRangeSymbol]: spawnRange,
      [spawnIntervalSymbol]: spawnInterval,
      [spawnTimeLeftSymbol]: spawnTimeLeft,
      [spawnMaxChildrenSymbol]: spawnMaxChildren,
    },
    act(
      this: Creature & {
        [spawnTemplateSymbol]: Partial<Creature> | 'self';
        [spawnRangeSymbol]: Decimal;
        [spawnIntervalSymbol]: Decimal;
        [spawnTimeLeftSymbol]: Decimal;
        [spawnMaxChildrenSymbol]: Decimal;
      }
    ) {
      this[spawnTimeLeftSymbol] = this[spawnTimeLeftSymbol].minus(1);
      if (this[spawnTimeLeftSymbol].lte(0)) {
        if (
          this[spawnMaxChildrenSymbol].gt(
            ri.actors.filter((actor) => actor.spawnedBy === this.name).length
          )
        ) {
          this[spawnTimeLeftSymbol] = this[spawnIntervalSymbol].times(
            Math.random()
          );
          // console.log('spawning', spawnTemplate);
          const actor = Creature.create({
            ...(spawnTemplate === 'self' ? this.breed : spawnTemplate),
            spawnedBy: this.name,
            position: this.position$.value.plus(
              Vector.random(this[spawnRangeSymbol])
            ),
          });
          if (actor.resources.health?.value?.gt(0)) {
            ri.addActor(actor);
          }
        }
      }
    },
  };
};

export const makeIntervalAction: (
  action: Action,
  interval: Decimal
) => Action = (action: Action, interval: Decimal) => ({
  properties: {
    intervalInterval: interval,
    intervalTimeLeft: interval,
    intervalAct: action.act,
    ...action.properties,
  },
  act(
    this: Creature & {
      intervalInterval: Decimal;
      intervalTimeLeft: Decimal;
      intervalAct: Action['act'];
      [key: keyof typeof action.properties]: unknown;
    }
  ) {
    this.intervalTimeLeft = this.intervalTimeLeft.minus(1);
    if (this.intervalTimeLeft.lte(0)) {
      this.intervalTimeLeft = this.intervalInterval;
      this.intervalAct.bind(this)();
    }
  },
});

export const makeConditionalAction: (
  condition: (actor: Actor) => boolean,
  actionProps: {[key: string]: unknown},
  actionIfTrue: (actor: Actor) => Action,
  actionIfFalse?: (actor: Actor) => Action
) => Action = (
  condition: (actor: Actor) => boolean,
  actionProps: {[key: string]: unknown},
  actionIfTrue: (actor: Actor) => Action,
  actionIfFalse: (actor: Actor) => Action = () => nullAction
) => {
  return {
    properties: {
      ...actionProps,
    },
    act(this: Creature) {
      if (condition(this)) {
        actionIfTrue(this).act.bind(this)();
      } else {
        actionIfFalse(this).act.bind(this)();
      }
    },
  };
};

export const makeOnceAction: (key: string, action: Action) => Action = (
  key: string,
  action: Action
) => {
  return {
    properties: {
      ...action.properties,
      [key]: false,
    },
    act(this: Creature) {
      if (!this[key]) {
        this[key] = true;
        action.act.bind(this)();
      }
    },
  };
};

export const makeRepeatAction = (action: Action, repeat: number) => ({
  properties: {...action.properties},
  act(this: Creature) {
    for (let i = 0; i < repeat; i++) {
      action.act.bind(this)();
    }
  },
});

export const makeTakeDamageAction = (damage: Decimal) => ({
  properties: {},
  act(this: Creature) {
    this.takeDamage(damage, this);
  },
});

export const makeChooseRandomAction = (actions: Action[]) => ({
  properties: {
    ...actions.reduce((acc, action) => ({...acc, ...action.properties}), {}),
  },
  act(this: Creature) {
    const chosenIndex = Math.floor(Math.random() * actions.length);
    const action = actions[chosenIndex];
    action.act.bind(this)();
  },
});

export const makeApplyStatusEffectToPlayerAction = (
  statusEffect: StatusEffect
) => ({
  properties: {},
  act(this: Creature) {
    if (ri.position.distance(this.position).lte(this.interactionRange)) {
      ri.addStatusEffect(statusEffect);
    }
  },
});

export const nullAction: Action = {
  act(this: Creature) {},
};
